import { StatusMap } from '@/components/data-table/constants';
import { Icons } from '@/components/icons';
import {
  defaultBooleanFilter,
  defaultDateFilter,
  defaultDateTimeFilter,
  defaultEnumFilter,
  defaultNullFilter,
  defaultNumberFilter,
  defaultRelationFilter,
  defaultSelectFilter,
  defaultStringFilter,
  defaultTagFilter,
  OPERATORS,
  RELATION_FILTER,
  type Filter,
} from '@/components/table-filters/constants';
import {
  ApartmentConfig,
  ApartmentContractRevenueRowConfig,
  ApartmentRevenueRowConfig,
  ArticleConfig,
  BuildingConfig,
  CommonAreaConfig,
  OrganizationUserConfig,
  OutdoorAreaConfig,
  RealEstateConfig,
  SegmentConfig,
} from '@pigello/pigello-matrix';

const baseFilters: Filter[] = [
  {
    ...defaultDateTimeFilter,
    title: 'Skapades',
    externalFieldName: 'created_at',
    defaultOperator: OPERATORS.DATE_TIME_PAST[0],
  },
];

export const baseMonitoringFilters: Filter[] = [
  {
    ...defaultSelectFilter,
    title: 'Typ',
    externalFieldName: 'mt_action_kind',
    choices: [
      { d: 'Skapades', v: 0 },
      { d: 'Uppdaterades', v: 1 },
      { d: 'Raderades', v: 2 },
    ],
  },
  {
    ...defaultStringFilter,
    title: 'Motivering',
    externalFieldName: 'mt_motivation',
  },
  {
    ...defaultDateFilter,
    title: 'Förfrågades',
    externalFieldName: 'mt_added_time',
  },
  {
    ...defaultRelationFilter,
    title: 'Förfrågades av användare',
    externalFieldName: 'mt_by_organization_user',
    relationModelName: OrganizationUserConfig.modelName,
    icon: Icons.user,
  },
];

export const baseTagFilter: Filter[] = [
  {
    ...defaultTagFilter,
    title: 'Taggar',
    externalFieldName: 'tags',
    relationModelName: 'tag',
    icon: Icons.tag,
  },
];

const SPACE_STATUS_FILTERS: Filter['enumChoices'] = [
  {
    filters: [{ operator: 'noop', value: 'vacant_no_future_contract' }],
    title: 'Nuvarande och kommande vakanser',
    badgeVariant: 'destructive',
    overrideExternalFieldName: 'custom_space_status_filter',
  },
  {
    filters: [{ operator: '__in', value: '12' }],
    title: StatusMap.object[12].text,
    badgeVariant: StatusMap.object[12].color,
  },
  {
    filters: [{ operator: '__in', value: '9' }],
    title: StatusMap.object[9].text,
    badgeVariant: 'blue',
  },
  {
    filters: [{ operator: '__in', value: '10' }],
    title: StatusMap.object[10].text,
    badgeVariant: StatusMap.object[10].color,
  },
  {
    filters: [{ operator: '__in', value: '11' }],
    title: StatusMap.object[11].text,
    badgeVariant: StatusMap.object[11].color,
  },
  {
    filters: [{ operator: '__in', value: '0' }],
    title: StatusMap.object[0].text,
    badgeVariant: StatusMap.object[0].color,
  },
  {
    filters: [{ operator: '__in', value: '1' }],
    title: StatusMap.object[1].text,
    badgeVariant: StatusMap.object[1].color,
  },
  {
    filters: [{ operator: '__in', value: '2' }],
    title: StatusMap.object[2].text,
    badgeVariant: StatusMap.object[2].color,
  },
  {
    filters: [{ operator: '__in', value: '3' }],
    title: StatusMap.object[3].text,
    badgeVariant: StatusMap.object[3].color,
  },
  {
    filters: [{ operator: '__in', value: '4' }],
    title: StatusMap.object[4].text,
    badgeVariant: StatusMap.object[4].color,
  },
  {
    filters: [{ operator: '__in', value: '5' }],
    title: StatusMap.object[5].text,
    badgeVariant: StatusMap.object[5].color,
  },
  {
    filters: [{ operator: '__in', value: '6' }],
    title: StatusMap.object[6].text,
    badgeVariant: StatusMap.object[6].color,
  },
  {
    filters: [{ operator: '__in', value: '7,8' }],
    title: StatusMap.object[7].text,
    badgeVariant: StatusMap.object[7].color,
  },

  {
    filters: [{ operator: '__in', value: '9,10,11,12' }],
    title: 'Samtliga vakanta statusar',
    badgeVariant: StatusMap.object[12].color,
  },
  {
    filters: [{ operator: '__in', value: '0,1,2,3,4,5,6,7,8' }],
    title: 'Samtliga uthyrda statusar',
    badgeVariant: StatusMap.object[0].color,
  },
  {
    title: 'Andrahandsuthyrt',
    filters: [{ operator: 'noop', value: 'sub_lease_active' }],
    badgeVariant: 'cyan',
    overrideExternalFieldName: 'custom_contract_filter',
  },
  {
    title: 'Borgensman',
    filters: [{ operator: 'noop', value: 'guarantor_active' }],
    badgeVariant: 'cyan',
    overrideExternalFieldName: 'custom_contract_filter',
  },
];

const SPACE_FUTURE_CONTRACTING_FILTERS: Filter['enumChoices'] = [
  {
    filters: [{ operator: '__in', value: '0' }],
    title: StatusMap['future-contracting'][0].text,
    badgeVariant: StatusMap['future-contracting'][0].color,
  },

  {
    filters: [{ operator: '__in', value: '1' }],
    title: StatusMap['future-contracting'][1].text,
    badgeVariant: StatusMap['future-contracting'][1].color,
  },

  {
    filters: [{ operator: '__in', value: '2' }],
    title: StatusMap['future-contracting'][2].text,
    badgeVariant: StatusMap['future-contracting'][2].color,
  },
  {
    filters: [{ operator: '__in', value: '3' }],
    title: StatusMap['future-contracting'][3].text,
    badgeVariant: StatusMap['future-contracting'][3].color,
  },
];

export const baseSpaceStatusFilters: Filter[] = [
  {
    ...defaultEnumFilter,
    title: ApartmentConfig.fields.status.verboseName,
    externalFieldName: ApartmentConfig.fields.status.externalFieldName,
    enumChoices: SPACE_STATUS_FILTERS,
  },
];

export const baseSpaceFutureContractingFilters: Filter[] = [
  {
    ...defaultEnumFilter,
    title: ApartmentConfig.fields.futureContracting.verboseName,
    externalFieldName:
      ApartmentConfig.fields.futureContracting.externalFieldName,
    enumChoices: SPACE_FUTURE_CONTRACTING_FILTERS,
  },
];

export const baseBuildingFilter: Filter[] = [
  {
    ...defaultRelationFilter,
    title: ApartmentConfig.fields.building.verboseName,
    externalFieldName: ApartmentConfig.fields.building.externalFieldName,
    relationModelName: BuildingConfig.modelName,
    icon: Icons.building,
  },
];
export const baseOutdoorAreaFilter: Filter[] = [
  {
    ...defaultRelationFilter,
    title: CommonAreaConfig.fields.outdoorArea.verboseName,
    externalFieldName: CommonAreaConfig.fields.outdoorArea.externalFieldName,
    relationModelName: OutdoorAreaConfig.modelName,
    icon: Icons.building,
  },
];
export const baseGlobalRealEstatesFilter: Filter[] = [
  {
    title: 'Fastigheter',
    externalFieldName: 'global_realestates',
    relationModelName: RealEstateConfig.modelName,
    kind: RELATION_FILTER,
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
    icon: Icons.realestate,
  },
];

export const baseGlobalSegmentsFilter: Filter[] = [
  {
    title: SegmentConfig.verboseName,
    externalFieldName: 'global_segments',
    relationModelName: SegmentConfig.modelName,
    kind: RELATION_FILTER,
    operators: [...OPERATORS.NOOP],
    defaultOperator: OPERATORS.NOOP[0],
    icon: Icons.segment,
  },
];

export const baseAddressFilters: Filter[] = [
  {
    ...defaultStringFilter,
    title: `${ApartmentConfig.fields.address.verboseName} - Gatunamn`,
    externalFieldName: `${ApartmentConfig.fields.address.externalFieldName}__street`,
    icon: Icons.address,
  },
  {
    ...defaultStringFilter,
    title: `${ApartmentConfig.fields.address.verboseName} - Stad`,
    externalFieldName: `${ApartmentConfig.fields.address.externalFieldName}__city`,
    icon: Icons.address,
  },
  {
    ...defaultStringFilter,
    title: `${ApartmentConfig.fields.address.verboseName} - Gatunummer`,
    externalFieldName: `${ApartmentConfig.fields.address.externalFieldName}__street_number`,
    icon: Icons.address,
  },
  {
    ...defaultStringFilter,
    title: `${ApartmentConfig.fields.address.verboseName} - Land`,
    externalFieldName: `${ApartmentConfig.fields.address.externalFieldName}__country`,
    icon: Icons.address,
  },
];

export const baseSpaceDebitRowFilters: Filter[] = [
  {
    ...defaultNullFilter,
    title: 'Renovering på objekt',
    externalFieldName:
      ApartmentRevenueRowConfig.fields.spaceRenovationNotation
        .externalFieldName,
    icon: Icons.renovation,
  },
  {
    ...defaultNullFilter,
    title: 'Renovering på struktur',
    externalFieldName:
      ApartmentRevenueRowConfig.fields.structureRenovationNotation
        .externalFieldName,
    icon: Icons.renovation,
  },

  {
    ...defaultNullFilter,
    title: 'Fördelningsavgift',
    externalFieldName:
      ApartmentRevenueRowConfig.fields.automaticDistributionQuota
        .externalFieldName,
    icon: Icons.fee,
  },
];

export const baseContractDebitRowFilters: Filter[] = [
  {
    ...defaultNullFilter,
    title: 'Retroaktiv debitering',
    externalFieldName:
      ApartmentContractRevenueRowConfig.fields.fromRetroactiveProposal
        .externalFieldName,
    icon: Icons.fee,
  },
  {
    ...defaultNullFilter,
    title: 'Indexuppräkning',
    externalFieldName:
      ApartmentContractRevenueRowConfig.fields.indexationUsage
        .externalFieldName,
    icon: Icons.indexation,
  },
  {
    ...defaultNullFilter,
    title: 'Indexuppräknas',
    externalFieldName:
      ApartmentContractRevenueRowConfig.fields.appliedIndexationUsage
        .externalFieldName,
    icon: Icons.indexation,
  },
  {
    ...defaultNullFilter,
    title: 'Omsättningshyra',
    externalFieldName:
      ApartmentContractRevenueRowConfig.fields.customerEarningsSetting
        .externalFieldName,
    icon: Icons.fee,
  },
  {
    ...defaultBooleanFilter,
    title: 'Böter från sen rapport av omsättning',
    externalFieldName:
      ApartmentContractRevenueRowConfig.fields.isCustomerEarningsReportPenalty
        .externalFieldName,
  },
  {
    ...defaultNullFilter,
    title: 'Fördelningsavgift',
    externalFieldName:
      ApartmentContractRevenueRowConfig.fields.automaticDistributionQuota
        .externalFieldName,
    icon: Icons.fee,
  },
];

export const baseDebitRowFilters: Filter[] = [
  {
    ...defaultStringFilter,
    title: ApartmentRevenueRowConfig.fields.title.verboseName,
    externalFieldName: ApartmentRevenueRowConfig.fields.title.externalFieldName,
  },
  {
    ...defaultRelationFilter,
    title: ApartmentRevenueRowConfig.fields.article.verboseName,
    externalFieldName:
      ApartmentRevenueRowConfig.fields.article.externalFieldName,
    relationModelName: ArticleConfig.modelName,
    icon: Icons.article,
  },
  {
    ...defaultNumberFilter,
    title: ApartmentRevenueRowConfig.fields.unitPrice.verboseName,
    externalFieldName:
      ApartmentRevenueRowConfig.fields.unitPrice.externalFieldName,
    icon: Icons.fee,
  },
  {
    ...defaultNumberFilter,
    title: ApartmentRevenueRowConfig.fields.unitAmount.verboseName,
    externalFieldName:
      ApartmentRevenueRowConfig.fields.unitAmount.externalFieldName,
    icon: Icons.fee,
  },
  {
    ...defaultNumberFilter,
    title: ApartmentRevenueRowConfig.fields.startDate.verboseName,
    externalFieldName:
      ApartmentRevenueRowConfig.fields.startDate.externalFieldName,
    icon: Icons.date,
  },
  {
    ...defaultNumberFilter,
    title: ApartmentRevenueRowConfig.fields.endDate.verboseName,
    externalFieldName:
      ApartmentRevenueRowConfig.fields.endDate.externalFieldName,
    icon: Icons.date,
  },
  {
    ...defaultStringFilter,
    title: ApartmentRevenueRowConfig.fields.customId.verboseName,
    externalFieldName:
      ApartmentRevenueRowConfig.fields.customId.externalFieldName,
    icon: Icons.id,
  },
  {
    ...defaultBooleanFilter,
    title:
      ApartmentRevenueRowConfig.fields.periodsAdjustsTotalAmount.verboseName,
    externalFieldName:
      ApartmentRevenueRowConfig.fields.periodsAdjustsTotalAmount
        .externalFieldName,
    icon: Icons.date,
  },
  {
    ...defaultSelectFilter,
    title: ApartmentRevenueRowConfig.fields.excemptFromMonths.verboseName,
    externalFieldName:
      ApartmentRevenueRowConfig.fields.excemptFromMonths.externalFieldName,
    choices: ApartmentRevenueRowConfig.fields.excemptFromMonths.choices,
    icon: Icons.date,
  },
  ...baseFilters,
];

export default baseFilters;
